/**
 * Dimension range filters — Atlantic Equipment
 *
 * Commercial kitchen buyers filter by size because they have a gap to fill.
 * The stock Shopify facet renders every distinct millimetre value as its own
 * checkbox: Width offered 34, Depth 41, Height 41. Nobody picks a gap out of
 * 41 exact heights.
 *
 * This upgrades any numeric list facet to a Min / Max pair of selects built
 * from the same values, then ticks the underlying checkboxes in that range and
 * lets the theme's own facet form submit as normal. Shopify's filtering is
 * untouched — this only changes the control the shopper touches.
 *
 * Progressive enhancement: if this script never runs, the checkboxes remain
 * and everything still works.
 */
(function () {
  'use strict';

  var MIN_VALUES_TO_ENHANCE = 8;

  function isNumeric(v) {
    return v !== '' && !isNaN(parseFloat(v)) && isFinite(v.replace(/,/g, ''));
  }

  function numeric(v) {
    return parseFloat(String(v).replace(/,/g, ''));
  }

  function buildSelect(label, values, selected, id) {
    var s = document.createElement('select');
    s.className = 'dim-range__select';
    s.id = id;
    var blank = document.createElement('option');
    blank.value = '';
    blank.textContent = label;
    s.appendChild(blank);
    values.forEach(function (v) {
      var o = document.createElement('option');
      o.value = String(v);
      o.textContent = String(v);
      if (selected !== null && numeric(selected) === v) o.selected = true;
      s.appendChild(o);
    });
    return s;
  }

  function enhance(details) {
    // The facet form re-renders the sidebar after every filter change. It keeps
    // the <details> element's own attributes — including this flag — but
    // replaces its children, so the flag alone lies. Trust the DOM: if the
    // selects are not actually present, this group needs enhancing again.
    if (details.dataset.dimRange === 'on' && details.querySelector('.dim-range__select')) {
      return;
    }

    var boxes = Array.prototype.slice.call(
      details.querySelectorAll('input[type="checkbox"]')
    );
    if (boxes.length < MIN_VALUES_TO_ENHANCE) return;
    if (!boxes.every(function (b) { return isNumeric(b.value); })) return;

    var values = boxes
      .map(function (b) { return numeric(b.value); })
      .sort(function (a, b) { return a - b; });

    var checked = boxes.filter(function (b) { return b.checked; })
      .map(function (b) { return numeric(b.value); })
      .sort(function (a, b) { return a - b; });

    var curMin = checked.length ? checked[0] : null;
    var curMax = checked.length ? checked[checked.length - 1] : null;

    var list = details.querySelector('ul, .facets-layout');
    if (!list) return;

    var base = 'DimRange-' + (details.id || Math.random().toString(36).slice(2));
    var wrap = document.createElement('div');
    wrap.className = 'dim-range';

    var minSel = buildSelect('No minimum', values, curMin, base + '-min');
    var maxSel = buildSelect('No maximum', values, curMax, base + '-max');

    var minLab = document.createElement('label');
    minLab.className = 'dim-range__label';
    minLab.setAttribute('for', minSel.id);
    minLab.textContent = 'Min';

    var maxLab = document.createElement('label');
    maxLab.className = 'dim-range__label';
    maxLab.setAttribute('for', maxSel.id);
    maxLab.textContent = 'Max';

    var minField = document.createElement('div');
    minField.className = 'dim-range__field';
    minField.appendChild(minLab);
    minField.appendChild(minSel);

    var maxField = document.createElement('div');
    maxField.className = 'dim-range__field';
    maxField.appendChild(maxLab);
    maxField.appendChild(maxSel);

    wrap.appendChild(minField);
    wrap.appendChild(maxField);

    var exact = document.createElement('button');
    exact.type = 'button';
    exact.className = 'dim-range__exact';
    exact.setAttribute('aria-expanded', 'false');
    exact.textContent = 'Choose exact sizes';

    // The checkbox list stays in the DOM: it is what actually submits, and a
    // shopper who wants three specific widths can still have them. The theme's
    // own "Show more" toggle belongs to that list, so it hides and shows with it
    // rather than floating under a collapsed control.
    var showMore = details.querySelector(
      'show-more-button, [id^="Show-More"], [id^="Show-more"]'
    );

    function setListVisible(visible) {
      list.hidden = !visible;
      if (showMore) showMore.hidden = !visible;
      exact.setAttribute('aria-expanded', String(visible));
      exact.textContent = visible ? 'Hide exact sizes' : 'Choose exact sizes';
    }

    setListVisible(false);
    exact.addEventListener('click', function () {
      setListVisible(list.hidden);
    });

    function apply() {
      var lo = minSel.value === '' ? -Infinity : numeric(minSel.value);
      var hi = maxSel.value === '' ? Infinity : numeric(maxSel.value);

      if (lo > hi && hi !== Infinity && lo !== -Infinity) {
        // Shopper picked a min above the max. Move the other handle rather
        // than silently returning nothing.
        if (this === minSel) { maxSel.value = minSel.value; hi = lo; }
        else { minSel.value = maxSel.value; lo = hi; }
      }

      var touched = null;
      boxes.forEach(function (b) {
        var n = numeric(b.value);
        var want = n >= lo && n <= hi && !(lo === -Infinity && hi === Infinity);
        if (b.checked !== want) { b.checked = want; touched = b; }
      });

      if (!touched) return;

      // The facet form listens for 'input' bubbling up from the checkboxes
      // themselves — a synthetic 'change' on the form is ignored. Fire both,
      // from ONE box we actually touched, so the sidebar makes a single AJAX
      // round trip rather than one per value in the range.
      touched.dispatchEvent(new Event('input', { bubbles: true }));
      touched.dispatchEvent(new Event('change', { bubbles: true }));
    }

    minSel.addEventListener('change', apply);
    maxSel.addEventListener('change', apply);

    list.parentNode.insertBefore(wrap, list);
    list.parentNode.insertBefore(exact, list);

    details.dataset.dimRange = 'on';
  }

  function run() {
    document
      .querySelectorAll('details.js-filter, .mobile-facets__details')
      .forEach(enhance);
  }

  document.addEventListener('DOMContentLoaded', run);
  // The facet form replaces the sidebar wholesale after every filter change.
  document.addEventListener('shopify:section:load', run);
  new MutationObserver(function () { run(); }).observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
